import ChevronIcon from "@/core/ui/iconsax/linear/arrow-right-1.svg"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import { useDashboardContext } from "@/dashboard/context/DashboardContext"
import useIsWebView from "@/product/util/hook/useIsWebView"
import styleIf from "@assets/style/util/styleIf"
import useShowOnHoverStyles from "@assets/style/util/useShowOnHoverStyles"
import Swiper, { SwiperProps } from "@components/carousel/Swiper"
import SwiperSlide from "@components/carousel/SwiperSlide"
import { DiscoIconButtonSkeleton, DiscoSection, DiscoTextSkeleton } from "@disco-ui"
import { DiscoCarouselItemSkeleton } from "@disco-ui/carousel/DiscoCarouselItem"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import DiscoText from "@disco-ui/text/DiscoText"
import { IconButton, useMediaQuery, useTheme } from "@material-ui/core"
import { ClassNameMap } from "@material-ui/core/styles/withStyles"
import { SkeletonProps } from "@material-ui/lab/Skeleton"
import { range } from "@utils/array/arrayUtils"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"
import React, { Fragment, useCallback, useMemo, useState } from "react"
import { Swiper as SwiperType } from "swiper"

export type DiscoCarouselProps<T> = TestIDProps & {
  classes?: Partial<ClassNameMap<"carousel" | "actions" | "slide" | "header" | "swiper">>
  /** The connection data */
  data: T[]
  /**
   * Number of visible slides in the carousel
   * prefer to pass `breakpoints` as this will set a fixed number of slides for all screen sizes
   */
  slidesPerView?: SwiperProps["config"]["slidesPerView"]
  centeredSlides?: boolean
  /**
   * Specify the number of slides that should be visible at designated breakpoints
   * @key {0} = xs+
   * @key {600} = sm+
   * @key {1280} = lg+
   * these are logical breakpoints that work with DiscoPage/Content
   */
  breakpoints?: NonNullable<SwiperProps["config"]>["breakpoints"]
  title?: string | React.ReactNode
  subTitle?: string | React.ReactNode
  /** Optional empty state, returns `null` by default if empty */
  emptyState?: React.ReactNode
  className?: string
  /** Additional CTA's beside the carousel navigation buttons */
  moreActions?: React.ReactNode | null
  /** Optional alternative starting index
   * @example
   * items = ["cat", "cat", "dog"]
   * condition = "dog"
   * @returns {Number} startingIndex = 2
   * Since we want to find the index which "dog" first appears in our array
   */
  startingIndex?: number
  totalCount: number
  refetch?: {
    isLoading: boolean
    hasNext: boolean
    hasPrevious: boolean
    loadMore: VoidFunction
  }
  /** Render method for each item */
  item: (item: T, index: number) => React.ReactElement<{ key: string }>
  itemSkeleton?: React.ReactElement
  disableNavButtons?: boolean
  padding?: number
  noVerticalMargin?: boolean
}

const DiscoCarousel = <T extends Record<string, any>>({
  title,
  subTitle,
  emptyState = null,
  className,
  moreActions,
  testid = "DiscoCarousel",

  startingIndex,
  slidesPerView,
  breakpoints,
  centeredSlides = true,

  totalCount,
  refetch,
  data,
  item: _renderItem,
  itemSkeleton,
  classes: customClasses,
  disableNavButtons = false,
  padding = 2,
  noVerticalMargin = false,
}: DiscoCarouselProps<T>) => {
  const [carouselState, setCarouselState] = useState<SwiperType | null>(null)
  const [nextEl, setNextEl] = useState<HTMLDivElement | null>(null)
  const [prevEl, setPrevEl] = useState<HTMLDivElement | null>(null)
  const onShowHoverClasses = useShowOnHoverStyles()
  const { isCommunityWelcome } = useDashboardContext() || {}
  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useMediaQuery(theme.breakpoints.up("sm"))
  const isWebView = useIsWebView()
  const getSlidesPerView = useCallback(() => {
    if (isWebView && isTablet) {
      return 3.1
    }
    if (isMobile || isWebView) {
      // if only 1 item, show full width, else show preview of next/prev item
      return totalCount === 1 ? 1 : 1.4
    }
    return slidesPerView
  }, [isMobile, isTablet, isWebView, totalCount, slidesPerView])

  const config = useMemo(() => {
    return {
      navigation: { nextEl, prevEl },
      initialSlide: startingIndex,
      slidesPerView: getSlidesPerView(),
      breakpoints,
      setParentCarouselState: setCarouselState,
      totalCount,
      currentSliceSize: data.length,
      centeredSlides,
    }
  }, [
    breakpoints,
    nextEl,
    prevEl,
    centeredSlides,
    startingIndex,
    data.length,
    totalCount,
    getSlidesPerView,
  ])

  const classes = useStyles({
    isBeginning: carouselState?.isBeginning || false,
    isEnd: carouselState?.isEnd || false,
    isCommunityWelcome,
    isShowingOneAtATime: slidesPerView === 1,
    centeredSlides,
  })
  const sectionPadding = {
    paddingTop: padding,
    paddingLeft: padding,
    paddingRight: padding,
    // bottom padding is 20px - 16px bottom margin on items for dropshadow = 4px
    paddingBottom: 0.5,
  }

  // fix issue where buttons are not disabled when all items are visible within the carousel track
  const navButtonsDisabled = carouselState?.virtual?.slides?.length
    ? carouselState.virtual.slides.length <= carouselState?.width
    : false
  const isNextButtonDisabled =
    (disableNavButtons || navButtonsDisabled) ?? carouselState?.isEnd
  const isPrevButtonDisabled =
    (disableNavButtons || navButtonsDisabled) ?? carouselState?.isBeginning

  const shouldShowHeader = title || moreActions

  return (
    <DiscoSection
      data-testid={testid}
      className={classNames(
        classes.paperContainer,
        onShowHoverClasses.hoverable,
        className
      )}
      {...sectionPadding}
    >
      {/* title and page indicator will overflow if title is too long */}
      {shouldShowHeader && (
        <div className={classNames(classes.header, customClasses?.header)}>
          <div className={classes.titleContainer}>
            {/* Title */}
            {title &&
              (typeof title === "string" ? (
                <DiscoText variant={"heading-xs-700"} testid={`${testid}.title`}>
                  {title}
                </DiscoText>
              ) : (
                title
              ))}
            {subTitle &&
              (typeof subTitle === "string" ? (
                <DiscoText
                  variant={"body-xs"}
                  truncateText={1}
                  component={"span"}
                  testid={`${testid}.subTitle`}
                >
                  {subTitle}
                </DiscoText>
              ) : (
                subTitle
              ))}
          </div>
          <div
            className={classNames(
              classes.actions,
              onShowHoverClasses.showable,
              customClasses?.actions
            )}
          >
            {/* Actions */}
            {moreActions}
          </div>
        </div>
      )}

      <div className={classes.swiperContainer}>
        {/* Items */}
        {data.length ? (
          <>
            <Swiper
              config={config}
              className={classNames(classes.carousel, customClasses?.carousel)}
              classes={{
                swiper: noVerticalMargin
                  ? classes.swiperNoVerticalMargin
                  : customClasses?.swiper,
              }}
            >
              {data.map((item, i) => (
                <SwiperSlide key={item.id} index={i} className={customClasses?.slide}>
                  {_renderItem(item, i)}
                </SwiperSlide>
              ))}

              {data.length !== totalCount && refetch?.hasNext && (
                <SwiperSlide>
                  <DiscoScrolledIntoView
                    className={classes.loadingContainer}
                    isLoading={refetch.isLoading}
                    onScrolledIntoView={refetch.loadMore}
                    skeleton={itemSkeleton ?? <DiscoCarouselItemSkeleton />}
                  />
                </SwiperSlide>
              )}
            </Swiper>
            {/* Back */}
            <div ref={(el) => setPrevEl(el)} className={classes.gradientPreviewPrev}>
              <IconButton
                className={classNames(classes.navButton, classes.prevNavButton)}
                data-testid={`${testid}.previous-button`}
                disabled={isPrevButtonDisabled}
              >
                <ChevronIcon className={classes.leftChevron} width={16} height={16} />
              </IconButton>
            </div>

            {/* Forward */}
            <div ref={(el) => setNextEl(el)} className={classes.gradientPreviewNext}>
              <IconButton
                className={classNames(classes.navButton, classes.nextNavButton)}
                data-testid={`${testid}.next-button`}
                disabled={isNextButtonDisabled}
              >
                <ChevronIcon className={classes.rightChevron} width={16} height={16} />
              </IconButton>
            </div>
          </>
        ) : (
          emptyState
        )}
      </div>
    </DiscoSection>
  )
}

interface StyleProps {
  isBeginning?: boolean
  isEnd?: boolean
  isCommunityWelcome?: boolean
  isShowingOneAtATime?: boolean
  centeredSlides?: boolean
}

const useStyles = makeUseStyles((theme) => ({
  paperContainer: {
    position: "relative",
    background: "none",
  },
  // Create a wrapper so we can center the back/next buttons
  swiperContainer: {
    position: "relative",
  },
  swiperNoVerticalMargin: {
    "& swiper-slide": {
      "& > *": {
        marginTop: 0,
        marginBottom: 0,
      },
    },
  },
  carousel: ({
    isBeginning,
    isEnd,
    isCommunityWelcome,
    isShowingOneAtATime,
  }: StyleProps) => ({
    // use a negative margin to extend the carousel to the edges of the DiscoSection
    // when only 1 item is visible, we want to respect the padding around the DiscoSection
    marginLeft: theme.spacing(-1),
    backgroundColor: isCommunityWelcome
      ? theme.palette.background.paper
      : theme.palette.background.default,
    borderRadius: theme.measure.borderRadius.big,
    position: "relative",

    "&::before": {
      height: "100%",
      position: "absolute",
      width: "30px",
      zIndex: 2,
      content: `""`,
      background: "transparent",
      maskImage: "none",
      pointerEvents: "none",
      ...styleIf(!isBeginning && !isShowingOneAtATime, {
        maskImage: "linear-gradient(to left, transparent 0%, black 100%)",
        background: "inherit",
      }),
    },
    "&::after": {
      height: "100%",
      position: "absolute",
      background: "transparent",
      width: "30px",
      zIndex: 2,
      content: `""`,
      right: 0,
      top: 0,
      maskImage: "none",
      pointerEvents: "none",
      ...styleIf(!isEnd && !isShowingOneAtATime, {
        maskImage: "linear-gradient(to right, transparent 0%, black 100%)",
        background: "inherit",
      }),
    },
  }),
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.25),
    alignItems: "center",
  },
  leftChevron: {
    transform: "rotate(180deg)",
    color:
      theme.palette.type === "dark"
        ? theme.palette.primary.contrastText
        : theme.palette.groovy.onDark[600],
  },
  rightChevron: {
    color:
      theme.palette.type === "dark"
        ? theme.palette.primary.contrastText
        : theme.palette.groovy.onDark[600],
  },
  navButton: {
    position: "absolute",
    zIndex: 2,
    borderRadius: theme.measure.borderRadius.medium,
    padding: theme.spacing(1),
    alignSelf: "center",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? theme.palette.groovy.neutral[500]
        : theme.palette.groovy.neutral[300]
    }`,

    transition: "background-color 0.4s ease",

    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.groovy.onDark[600]
        : theme.palette.primary.contrastText,
    boxShadow: theme.palette.groovyDepths.boxShadow,

    "&.swiper-button-disabled, &.swiper-button-lock, &.Mui-disabled": {
      display: "none",
    },

    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? theme.palette.groovy.neutral[400]
          : theme.palette.groovy.neutral[200],
    },
  },
  prevNavButton: {
    left: 0,
  },
  nextNavButton: {
    right: 0,
  },
  loadingContainer: {
    display: "flex",
  },
  gradientPreviewPrev: ({
    isBeginning,
    isShowingOneAtATime,
    centeredSlides,
  }: StyleProps) => ({
    width: "60px",
    height: "100%",
    backgroundColor: "transparent",
    position: "absolute",
    zIndex: 2,
    top: 0,
    cursor: "pointer",
    display: "flex",
    left: isShowingOneAtATime || !centeredSlides ? -16 : -24, // When the cards aren't centered, this makes sure the icon is aligned with the left edge of the card

    ...styleIf(isBeginning, {
      display: "none",
    }),
  }),
  gradientPreviewNext: ({ isEnd, isShowingOneAtATime }: StyleProps) => ({
    width: "60px",
    height: "100%",
    backgroundColor: "transparent",
    position: "absolute",
    zIndex: 2,
    top: 0,
    cursor: "pointer",
    display: "flex",
    right: isShowingOneAtATime ? -8 : -16,

    ...styleIf(isEnd, {
      display: "none",
    }),
  }),
}))

type DiscoCarouselSkeletonProps = Omit<
  DiscoCarouselProps<any>,
  "data" | "Item" | "totalCount" | "refetch" | "title" | "item"
> &
  SkeletonProps & {
    item: React.ReactElement
    showTitle?: boolean
    showHeader?: boolean
  }

export const DiscoCarouselSkeleton: React.FC<DiscoCarouselSkeletonProps> = ({
  item,
  showTitle = false,
  showHeader = true,
  title,
  ...props
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()

  // handle responsive slidesPerView
  const slidesPerView = isMobile
    ? 1
    : typeof props.slidesPerView === "number"
    ? props.slidesPerView
    : 3

  const classes = useStyles({
    isBeginning: false,
    isEnd: false,
    isCommunityWelcome: false,
  })
  return (
    <DiscoSection
      className={classNames(classes.paperContainer, props.className)}
      padding={2.5}
    >
      {showHeader && (
        <div className={classes.header} style={{ width: "100%" }}>
          {title ? (
            <DiscoText variant={"body-lg-600"}>{title}</DiscoText>
          ) : (
            showTitle && <DiscoTextSkeleton variant={"body-lg-600"} width={"60%"} />
          )}

          <div
            className={classNames(classes.actions, props.classes?.actions)}
            style={{ justifyContent: "flex-end" }}
          >
            <DiscoIconButtonSkeleton width={24} height={24} />
            <DiscoIconButtonSkeleton width={24} height={24} />
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gap: theme.spacing(2),
          width: "100%",
          gridTemplateColumns: `repeat(${slidesPerView}, 1fr)`,
        }}
      >
        {range(Math.round(slidesPerView)).map((i) => (
          <Fragment key={i}>{item}</Fragment>
        ))}
      </div>
    </DiscoSection>
  )
}

export default DiscoCarousel
